import { throttle, template } from 'lodash'
import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import { Transition } from 'react-transition-group'

import Icon from 'components/Icon'
import { ResizeContext } from 'contexts/Resize'

import {
  Inner,
  Item,
  Title,
  Arrow,
  Body,
  BodyInner,
  TextBox,
  Plus,
  Stick,
  TextLHStyled,
} from './styles'

const TRANSITION_TIMEOUT = 300

const indicatorsTheme = {
  dark: {
    arrow: 'white',
    plus: 'white',
  },
  light: {
    arrow: 'black',
    plus: 'black',
  },
  transparent: {
    arrow: 'black',
    plus: 'black',
  },
}

/**
 * Accordion component - component with a standard accordion behavior stylized for FAQ
 *
 * @return {Component} FaqAccordion
 */

class FaqAccordion extends PureComponent {
  static contextType = ResizeContext

  constructor(props) {
    super(props)

    this.accordionItemsRefs = props.items.map(() => React.createRef())
    this.accordionTitlesRefs = props.items.map(() => React.createRef())
    this.throttledUpdateHeight = throttle(this.updateHeight, 200)
    this.state = {
      openedItemIndex: props.openedItemIndex,
      accordionBodyHeight: 0,
      menuHeight: 0,
      animationActive: false,
    }
  }

  componentDidMount() {
    this.updateHeight()
    window.addEventListener('resize', this.throttledUpdateHeight)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledUpdateHeight)
  }

  /**
   *
   * @param {Number} itemIndex - index of active item
   * @param {element} element - reference to DOM node
   * @return {function} Function — setState
   */
  toggleHandler = (itemIndex, element, title) => {
    const { openedItemIndex, accordionBodyHeight, menuHeight } = this.state

    if (!element || !element.current) {
      return null
    }

    const { height } = element.current.getBoundingClientRect()

    if (itemIndex === openedItemIndex) {
      return this.setState({
        animationActive: true,
        openedItemIndex: null,
        accordionBodyHeight: 0,
      })
    }

    if (this.context.mobile && menuHeight) {
      const titleBBox = title.current.getBoundingClientRect()
      let nextPosition = window.pageYOffset + titleBBox.top - menuHeight
      if (openedItemIndex !== null && openedItemIndex < itemIndex) {
        nextPosition -= accordionBodyHeight
      }

      window.scrollTo(0, Math.floor(nextPosition))
    }

    return this.setState({
      animationActive: true,
      openedItemIndex: itemIndex,
      accordionBodyHeight: height,
    })
  }

  updateHeight = () => {
    const { menuNode } = this.props
    const newMenuHeight = menuNode ? menuNode.getBoundingClientRect().height : 0

    const { openedItemIndex, menuHeight } = this.state
    let actualHeight = 0

    if (
      (openedItemIndex || openedItemIndex !== null) &&
      this.accordionItemsRefs[openedItemIndex] &&
      this.accordionItemsRefs[openedItemIndex].current !== null
    ) {
      actualHeight = this.accordionItemsRefs[
        openedItemIndex
      ].current.getBoundingClientRect().height
      this.setState({
        accordionBodyHeight: actualHeight,
        menuHeight: newMenuHeight,
      })
      return null
    }

    const isNotUnmounted =
      this.accordionItemsRefs[0] && this.accordionItemsRefs[0].current

    if (newMenuHeight !== menuHeight && isNotUnmounted)
      this.setState({
        menuHeight: newMenuHeight,
      })

    return
  }

  handleAnimationEnd = () => {
    const { onChange } = this.props
    const { openedItemIndex } = this.state

    this.setState(
      {
        animationActive: false,
      },
      () => {
        if (onChange) {
          onChange(openedItemIndex)
        }
      },
    )
  }

  render() {
    const { items, locale, colorTheme, indicator } = this.props
    const { openedItemIndex, accordionBodyHeight } = this.state
    const accordionItems = items.map((value, key) => {
      const compiled = template(value.content)

      return (
        <Item isOpened={openedItemIndex === key} key={key} {...{ colorTheme }}>
          <Inner
            ref={this.accordionTitlesRefs[key]}
            {...{ colorTheme, indicator }}
            onClick={() =>
              this.toggleHandler(
                key,
                this.accordionItemsRefs[key],
                this.accordionTitlesRefs[key],
              )
            }
          >
            <Title>
              <TextLHStyled
                as="h3"
                indicator={indicator}
                dangerouslySetInnerHTML={{ __html: value.title }}
              />
            </Title>
            {indicator === 'arrow' ? (
              <Arrow>
                <Icon
                  id="arrow_down"
                  color={indicatorsTheme[colorTheme][indicator]}
                />
              </Arrow>
            ) : (
              <Plus background={indicatorsTheme[colorTheme][indicator]}>
                <Stick />
                <Stick isVertical={true} />
              </Plus>
            )}
          </Inner>
          <Body {...{ colorTheme }} isHidden={openedItemIndex !== key}>
            <BodyInner
              isHeightChange={openedItemIndex === key}
              {...{ accordionBodyHeight }}
            >
              <TextBox ref={this.accordionItemsRefs[key]}>
                <TextLHStyled
                  className="with-inverted-links"
                  indicator={indicator}
                  dangerouslySetInnerHTML={{
                    __html: compiled({ locale }),
                  }}
                />
              </TextBox>
            </BodyInner>
          </Body>
        </Item>
      )
    })

    return (
      <Transition
        onEntered={this.handleAnimationEnd}
        onExited={this.handleAnimationEnd}
        in={this.state.animationActive}
        timeout={TRANSITION_TIMEOUT}
      >
        <div>{accordionItems}</div>
      </Transition>
    )
  }
}

FaqAccordion.propTypes = {
  /** List of items for a single accordion section */
  items: PropTypes.array.isRequired,

  /** Locale for links inside texts */
  locale: PropTypes.string.isRequired,

  menuNode: PropTypes.object,

  /** Opened item index */
  openedItemIndex: PropTypes.number,

  /** Function which called when a state of an accordion item changed */
  onChange: PropTypes.func,

  /** Indicator */
  indicator: PropTypes.oneOf(['arrow', 'plus']),

  /** Color theme of the component */
  colorTheme: PropTypes.oneOf(['dark', 'light', 'transparent']),
}

FaqAccordion.defaultProps = {
  indicator: 'arrow',
  colorTheme: 'light',
}

export default FaqAccordion
